import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();

  const isAuthenticated = !!user;
  const userName = userProfile?.full_name || user?.email || 'User';

  const navigationItems = [
    { label: 'Home', path: '/home-directory', icon: 'Home' },
    { label: 'Search', path: '/search-results', icon: 'Search' },
    { label: 'Add Listing', path: '/add-business', icon: 'Plus', authenticated: true },
  ];

  const handleNavigation = (path, requiresAuth = false) => {
    if (requiresAuth && !isAuthenticated) {
      setAuthMode('login');
      setShowAuthModal(true);
      return;
    }
    navigate(path);
    setIsMenuOpen(false);
  };

  const handleAuthAction = () => {
    if (isAuthenticated) {
      navigate('/user-profile');
    } else {
      setAuthMode('login');
      setShowAuthModal(true);
      navigate('/login');

    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/home-directory');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const switchAuthMode = () => {
    setAuthMode(authMode === 'login' ? 'register' : 'login');
  };

  const handleAuthSubmit = (e) => {
    e?.preventDefault();
    // This is handled by the AuthContext now
    setShowAuthModal(false);
    navigate('/user-profile');
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div
              className="flex items-center cursor-pointer transition-organic hover:opacity-80"
              onClick={() => navigate('/home-directory')}
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Leaf" size={20} color="white" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-xl font-heading font-semibold text-foreground">
                    Holistic Directories
                  </h1>
                  <p className="text-xs text-muted-foreground font-caption -mt-1">
                    Connect. Practice. Evolve.
                  </p>
                </div>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigationItems?.map((item) => {
                if (item?.authenticated && !isAuthenticated) return null;

                const isActive = location?.pathname === item?.path;
                return (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path, item?.authenticated)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-body font-medium transition-organic ${
                      isActive
                        ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
                    }`}
                  >
                    <Icon name={item?.icon} size={18} />
                    <span>{item?.label}</span>
                  </button>
                );
              })}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-4">
              {/* Search Icon */}
              <button
                onClick={() => navigate('/search-results')}
                className="p-2 rounded-lg text-foreground hover:text-primary hover:bg-primary/5 transition-organic"
                aria-label="Search"
              >
                <Icon name="Search" size={20} />
              </button>

              {/* Auth/Profile */}
              {isAuthenticated ? (
                <div className="relative group">
                  <button
                    onClick={handleAuthAction}
                    className="flex items-center space-x-2 p-2 rounded-lg hover:bg-primary/5 transition-organic"
                  >
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="User" size={16} color="white" />
                    </div>
                    <span className="hidden sm:block text-sm font-medium text-foreground">
                      {userName}
                    </span>
                  </button>

                  {/* Profile Dropdown */}
                  <div className="absolute right-0 top-full mt-2 w-56 bg-popover border border-border rounded-lg shadow-organic-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-organic">
                    <div className="p-2">
                      <div className="px-3 py-2 border-b border-border">
                        <p className="text-sm font-medium text-foreground">{userName}</p>
                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/user-profile');
                          setIsMenuOpen(false);
                        }}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-gentle"
                      >
                        <Icon name="User" size={16} />
                        <span>Profile</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md transition-gentle"
                      >
                        <Icon name="LogOut" size={16} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAuthAction}
                  iconName="LogIn"
                  iconPosition="left"
                  iconSize={16}
                >
                  Sign In
                </Button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden p-2 rounded-lg text-foreground hover:text-primary hover:bg-primary/5 transition-organic"
                aria-label="Menu"
              >
                <Icon name={isMenuOpen ? "X" : "Menu"} size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-background border-t border-border">
            <div className="px-4 py-4 space-y-2">
              {navigationItems?.map((item) => {
                if (item?.authenticated && !isAuthenticated) return null;

                const isActive = location?.pathname === item?.path;
                return (
                  <button
                    key={item?.path}
                    onClick={() => handleNavigation(item?.path, item?.authenticated)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-organic ${
                      isActive
                        ? 'text-primary bg-primary/10' :'text-foreground hover:text-primary hover:bg-primary/5'
                    }`}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span className="font-body font-medium">{item?.label}</span>
                  </button>
                );
              })}

              {/* Mobile Auth Actions */}
              {isAuthenticated ? (
                <>
                  <div className="border-t border-border my-2"></div>
                  <button
                    onClick={() => {
                      navigate('/user-profile');
                      setIsMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-organic text-foreground hover:text-primary hover:bg-primary/5"
                  >
                    <Icon name="User" size={20} />
                    <span className="font-body font-medium">Profile</span>
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-organic text-foreground hover:text-primary hover:bg-primary/5"
                  >
                    <Icon name="LogOut" size={20} />
                    <span className="font-body font-medium">Sign Out</span>
                  </button>
                </>
              ) : (
                <div className="border-t border-border my-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      handleAuthAction();
                      setIsMenuOpen(false);
                    }}
                    iconName="LogIn"
                    iconPosition="left"
                    iconSize={16}
                    className="w-full"
                  >
                    Sign In
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
